import { t as tCem } from '../utils/i18n'
import { useState, useEffect } from 'react'
import { useAuthStore } from '../store/authStore'

const TYPE_ICON  = { camera: '◉', speaker: '♪', panel: '▣', nvr: '▤', sensor: '⬡', access: '⊞' }
const TYPE_LABEL = { camera: 'Cámara', speaker: 'Bocina', panel: 'Panel', nvr: 'NVR', sensor: 'Sensor', access: 'Acceso' }

function StatusDot({ online }) {
  return (
    <span style={{
      width: 6, height: 6, borderRadius: '50%', flexShrink: 0,
      background: online ? 'var(--success)' : 'var(--danger)',
      display: 'inline-block',
    }} />
  )
}

function SiteRow({ site, devices, onCameraClick }) {
  const [open, setOpen] = useState(false)
  const cams    = devices.filter(d => d.type === 'camera')
  const offline = devices.filter(d => !d.online).length

  return (
    <div>
      <div
        onClick={() => setOpen(o => !o)}
        style={{
          display: 'flex', alignItems: 'center', gap: 5,
          padding: '4px 6px', borderRadius: 'var(--radius-sm)',
          cursor: 'pointer', fontSize: 12,
          background: open ? 'var(--bg-active)' : 'transparent',
          color: open ? 'var(--accent)' : 'var(--text-primary)',
        }}
      >
        <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>{open ? '▾' : '▸'}</span>
        <span style={{ fontSize: 13 }}>⛽</span>
        <span className="truncate" style={{ flex: 1 }}>{site.name}</span>
        {offline > 0
          ? <span style={{ fontSize: 10, color: 'var(--danger)', fontWeight: 600 }}>{offline} off</span>
          : <StatusDot online={true} />}
      </div>

      {open && (
        <div style={{ paddingLeft: 18 }}>
          {devices.map(dev => (
            <div
              key={dev.id}
              onClick={() => dev.type === 'camera' && onCameraClick?.(dev)}
              style={{
                display: 'flex', alignItems: 'center', gap: 5,
                padding: '3px 6px', borderRadius: 'var(--radius-sm)',
                cursor: dev.type === 'camera' ? 'pointer' : 'default',
                fontSize: 11,
              }}
              title={dev.type === 'camera' ? 'Click: preview · Doble click: abrir stream' : dev.name}
              onDoubleClick={() => dev.type === 'camera' && onCameraClick?.(dev, true)}
            >
              <StatusDot online={dev.online} />
              <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                {TYPE_ICON[dev.type] || '○'}
              </span>
              <span className="truncate" style={{ flex: 1, color: 'var(--text-secondary)' }}>
                {dev.name}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

function ZoneRow({ zone, sites, devicesBySite, onCameraClick }) {
  const [open, setOpen] = useState(true)
  return (
    <div style={{ marginBottom: 2 }}>
      <div
        onClick={() => setOpen(o => !o)}
        style={{
          display: 'flex', alignItems: 'center', gap: 5,
          padding: '4px 6px', borderRadius: 'var(--radius-sm)',
          cursor: 'pointer', fontSize: 12, fontWeight: 500,
          color: 'var(--text-secondary)',
        }}
      >
        <span style={{ fontSize: 10 }}>{open ? '▾' : '▸'}</span>
        <span>📍</span>
        <span>{zone.name}</span>
        <span style={{ marginLeft: 'auto', fontSize: 10, color: 'var(--text-muted)' }}>
          {sites.length} sitios
        </span>
      </div>
      {open && (
        <div style={{ paddingLeft: 10 }}>
          {sites.map(site => (
            <SiteRow
              key={site.id} site={site}
              devices={devicesBySite[site.id] || []}
              onCameraClick={onCameraClick}
            />
          ))}
        </div>
      )}
    </div>
  )
}

export default function DevicePanel({ collapsed, onToggle, onCameraClick }) {
  const [tree, setTree]       = useState([])
  const [devStats, setStats]  = useState(null)
  const [search, setSearch]   = useState('')
  const [devBySite, setDevBySite] = useState({})
  const [lastUpdate, setLastUpdate] = useState(null)
  const token = useAuthStore(s => s.token)

  useEffect(() => {
    if (!token) return
    const headers = { Authorization: `Bearer ${token}` }

    const loadTree = async () => {
      const [treeRes, statsRes] = await Promise.all([
        fetch('/api/sites/tree', { headers }),
        fetch('/api/devices/stats', { headers }),
      ])
      if (treeRes.ok)  setTree(await treeRes.json())
      if (statsRes.ok) setStats(await statsRes.json())
      setLastUpdate(new Date())
    }

    loadTree()
    const interval = setInterval(loadTree, 30000)
    return () => clearInterval(interval)
  }, [token])

  if (collapsed) return (
    <aside
      onClick={onToggle}
      style={{
        width: 20, background: 'var(--bg-surface)',
        borderLeft: '1px solid var(--border)', cursor: 'pointer',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        color: 'var(--text-muted)', fontSize: 10, userSelect: 'none',
        flexShrink: 0,
      }}
      title="Abrir panel de dispositivos"
    >›</aside>
  )

  const fmtTime = d => d ? `${d.getHours()}:${String(d.getMinutes()).padStart(2,'0')}` : '--'

  return (
    <aside style={{
      width: 'var(--device-panel-w)', background: 'var(--bg-surface)',
      borderLeft: '1px solid var(--border)',
      display: 'flex', flexDirection: 'column', flexShrink: 0,
    }}>
      {/* Header */}
      <div style={{
        padding: '8px 12px', borderBottom: '1px solid var(--border)',
        display: 'flex', alignItems: 'center', gap: 6,
      }}>
        <span style={{ fontSize: 13 }}>◉</span>
        <span style={{ fontSize: 12, fontWeight: 500, flex: 1 }}>Dispositivos</span>
        {devStats && (
          <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>
            {(devStats.cameras.online + devStats.speakers.online + devStats.panels.online)} en línea
          </span>
        )}
        <button
          onClick={onToggle}
          style={{
            background: 'transparent', border: 'none',
            cursor: 'pointer', color: 'var(--text-muted)', fontSize: 14, lineHeight: 1,
          }}
          title={tCem('Ocultar panel','Hide panel')}
        >‹</button>
      </div>

      {/* Stats rápidos */}
      {devStats && (
        <div style={{
          display: 'grid', gridTemplateColumns: '1fr 1fr 1fr',
          padding: '8px', gap: 4, borderBottom: '1px solid var(--border)',
        }}>
          {[
            { label: tCem('Cámaras','Cameras'), ...devStats.cameras, icon: '◉' },
            { label: 'Bocinas', ...devStats.speakers, icon: '♪' },
            { label: 'Paneles', ...devStats.panels,   icon: '▣' },
          ].map(stat => (
            <div key={stat.label} style={{
              background: 'var(--bg-muted)', borderRadius: 'var(--radius-sm)',
              padding: '5px 6px', textAlign: 'center',
            }}>
              <div style={{ fontSize: 10, color: 'var(--text-muted)', marginBottom: 2 }}>{stat.label}</div>
              <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-primary)' }}>
                {stat.online}
                {stat.offline > 0 && (
                  <span style={{ color: 'var(--danger)', fontSize: 10 }}> -{stat.offline}</span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Search */}
      <div style={{ padding: '8px', borderBottom: '1px solid var(--border)' }}>
        <input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Buscar sitio o cámara…"
          style={{
            width: '100%', padding: '5px 8px', fontSize: 12,
            background: 'var(--bg-muted)', border: '1px solid var(--border)',
            borderRadius: 'var(--radius-sm)', color: 'var(--text-primary)',
            outline: 'none',
          }}
        />
      </div>

      {/* Tree */}
      <div style={{ flex: 1, overflow: 'auto', padding: '6px 6px' }}>
        {tree.map(fmt => (
          <div key={fmt.id} style={{ marginBottom: 8 }}>
            <div style={{
              fontSize: 11, fontWeight: 600, color: 'var(--text-muted)',
              padding: '4px 6px', textTransform: 'uppercase', letterSpacing: '0.06em',
              display: 'flex', alignItems: 'center', gap: 4,
            }}>
              <span>🏪</span> {fmt.name}
            </div>
            {fmt.zones?.map(zone => {
              const sites = zone.sites?.filter(s =>
                !search || s.name.toLowerCase().includes(search.toLowerCase())
              ) || []
              if (search && sites.length === 0) return null
              return (
                <ZoneRow
                  key={zone.id} zone={zone} sites={sites}
                  devicesBySite={devBySite}
                  onCameraClick={onCameraClick}
                />
              )
            })}
          </div>
        ))}
      </div>

      {/* Footer */}
      <div style={{
        padding: '6px 12px', borderTop: '1px solid var(--border)',
        display: 'flex', justifyContent: 'space-between',
        fontSize: 10, color: 'var(--text-muted)',
      }}>
        <span>Actualizado {fmtTime(lastUpdate)}</span>
        <span style={{ cursor: 'pointer' }} onClick={() => setLastUpdate(null)}>↺</span>
      </div>
    </aside>
  )
}
